// Import utilities for the api.
import { currentTime, off, on, storageAvailable } from './Helpers';

/**
 * Manages has visited page tracking for the app.
 *
 * @since 5.13.x
 */
export default class LastSeen {
	/**
	 * The key used for storing the first visit object.
	 *
	 * @since 5.13.x
	 */
	static lastSeenKey = 'omLastSeen';

	/**
	 * Set the last seen time for the specified campaign type in `localStorage`.
	 *
	 * The `any` key will always hold the last seen time of any campaign type.
	 *
	 * @since 5.13.x
	 *
	 * @param {string} type The campaign type who's last seen time should be set.
	 *
	 * @returns {void}
	 */
	static set = (type) => {
		// Bail if `localStorage` is unavailable.
		if (!storageAvailable('localStorage')) {
			return;
		}

		let storage = window.localStorage;
		let lastSeen = JSON.parse(storage.getItem(LastSeen.lastSeenKey) || '{}');
		let time = currentTime();

		// Set the campaign type time, and the any key.
		lastSeen[type] = time;
		lastSeen.any = time;

		// Set the updated last seen time object to `localStorage`.
		storage.setItem(LastSeen.lastSeenKey, JSON.stringify(lastSeen));
	};

	/**
	 * Get the specified item from `localStorage`.
	 *
	 * We don't check for the availability of storage here. Since we are not
	 * writing, try/catch is sufficient.
	 *
	 * @since 5.13.x
	 *
	 * @param {string} type The last seen time type to retrieve.
	 *
	 * @returns {number|null} The `localStorage` key value.
	 */
	static get = (type) => {
		try {
			let storage = window.localStorage;
			let lastSeen = JSON.parse(storage.getItem(LastSeen.lastSeenKey) || '{}');

			return lastSeen[type] && !isNaN(parseInt(lastSeen[type])) ? parseInt(lastSeen[type]) : null;
		} catch (e) {
			return null;
		}
	};

	/**
	 * Delete the last seen `localStorage` key.
	 *
	 * We don't check for the availability of storage here. Since we are not
	 * writing, try/catch is sufficient.
	 *
	 * @since 5.13.x
	 *
	 * @returns {void}
	 */
	static delete = () => {
		try {
			let storage = window.localStorage;
			storage.removeItem(LastSeen.lastSeenKey);
		} catch (e) {}
	};

	/**
	 * Triggers an analytics tracking event with the impression type.
	 *
	 * @since 5.13.x
	 *
	 * @param {Object} C The campaign object.
	 *
	 * @returns {void|Function} Will return scrollOff if this is not a valid campaign type.
	 */
	static scrollOn = (C) => {
		on(window, 'scroll.omLastSeen.' + C.id, () => {
			// This should never happen, but just in case.
			if (!C.is() || !C.Types.isInline()) {
				return LastSeen.scrollOff(C);
			}

			if (LastSeen.visible(C)) {
				LastSeen.set('inline');
				LastSeen.scrollOff(C);
			}
		});
	};

	/**
	 * Check if the inline campaign is visible.
	 *
	 * @since 5.13.x
	 *
	 * @param {Object} C The campaign object.
	 *
	 * @returns {boolean} Whether or not the campaign is visible.
	 */
	static visible = (C) => {
		return 49 < C.Types.visible().percent;
	};

	/**
	 * Turns off the scroll check.
	 *
	 * @since 5.13.x
	 *
	 * @param {Object} C The campaign object.
	 *
	 * @returns {void}
	 */
	static scrollOff = (C) => {
		off(window, 'scroll.omLastSeen.' + C.id);
	};
}
